import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ensureCurrentUser } from '../../../util/data';
import { getCoachPayoutAccountStatusApi } from '../../../util/api';

/**
 * Hook to check the Stripe connection and verification status of the
 * current user. Sources from this app's own CoachPayoutAccount (the same
 * Connect account Subscriptions/Donations/the native booking engine use) --
 * NOT Sharetribe's own Connect account, which is a separate account under a
 * different Stripe platform and can't receive transfers from this app's
 * Stripe platform. This is the single, unified payout-connection gate for
 * every coach-selling surface (sessions, subscriptions, donations, camps,
 * classes, native bookings).
 *
 * @returns {Object} Stripe status flags
 * @returns {boolean} stripeConnected - Whether the coach has a payout account connected
 * @returns {boolean} requirementsMissing - Whether onboarding was started but not completed
 * @returns {boolean} isStripeReady - Whether the coach is fully ready to receive payouts
 */
const useStripeStatus = () => {
    const currentUser = useSelector(state => state.user.currentUser);
    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const currentUserLoaded = !!ensuredCurrentUser.id;

    const [payoutAccount, setPayoutAccount] = useState(null);
    const [statusLoaded, setStatusLoaded] = useState(false);

    const fetchStatus = useCallback(async () => {
        try {
            const response = await getCoachPayoutAccountStatusApi();
            setPayoutAccount(response || null);
        } catch (e) {
            setPayoutAccount(null);
        } finally {
            setStatusLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (!currentUserLoaded) return;
        fetchStatus();
    }, [currentUserLoaded, fetchStatus]);

    const stripeConnected = !!payoutAccount?.connected;
    const requirementsMissing = stripeConnected && !payoutAccount?.transfersActive;
    const isStripeReady = stripeConnected && Boolean(payoutAccount?.transfersActive);

    return {
        stripeConnected,
        requirementsMissing,
        isStripeReady,
        stripeAccountData: payoutAccount,
        statusLoaded,
    };
};

export default useStripeStatus;
